import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Spinner,
  Button,
  Container,
  Alert,
  Modal,
  Dropdown,
  Accordion,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import {
  getInvoiceById,
  createClaim,
  getClaimByInvoiceId,
  updateClaim,
  updateInvoice,
  sendInvoiceByEmail,
  getBankingDetailsByPracticeNumbers,
  decryptField,
} from "../../afinanceapi";
import { useParams, useLocation } from "react-router-dom";
import { Breadcrumb } from "../Breadcrumb/Breadcrumb";
import {
  getAccountById,
  getPatientById,
  getPracticeById,
  getUserById,
} from "../../api";
import { SubmitClaim } from "./SubmitClaim";
import { ReverseClaim } from "./ReverseClaim";
import { useNavigate } from "react-router-dom";
import { ExportPDFInvoice } from "./ExportInvoicePDF";
import CreateReceiptModal from "./Receipt";
import { jwtDecode } from "jwt-decode";
import EmailInvoiceModal from "./EmailInvoice";
import { buildEmailData } from "./BuildInvRecCNData";
import ChangeLiability from "./ChangeLiability";
import InvoiceDeletionModal from "./InvoiceDeletionModal";
import ReceiptReversalModal from "./ReceiptReversalModal";
import { isLineDateOnly } from "../../utils/appointmentTimes";
import "./acc-list.css";
import "./acc-rail.css";

export default function PostedInvoices(onOpenReceipt) {
  const [patTotal, setPatTotal] = useState(0);
  const [medTotal, setMedTotal] = useState(0);
  const [patReceiptTotal, setPatReceiptTotal] = useState(0);
  const [medReceiptTotal, setMedReceiptTotal] = useState(0);
  const [patCreditTotal, setPatCreditTotal] = useState(0);
  const [medCreditTotal, setMedCreditTotal] = useState(0);
  const [patRefundTotal, setPatRefundTotal] = useState(0);
  const [medRefundTotal, setMedRefundTotal] = useState(0);
  const [invoice, setInvoice] = useState({});
  const [account, setAccount] = useState({});
  const [patient, setPatients] = useState({});
  const [practice, setPractice] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [practitioner, setPractitioner] = useState({});
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const navigate = useNavigate();
  const [loadingClaim, setLoadingClaim] = useState(false);
  const [userId, setUserId] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false);
  const [message, setMessage] = useState("");
  const [statementBlob, setStatementBlob] = useState(null);
  const [showLineInfo, setShowLineInfo] = useState(false);
  const [selectedClaimLine, setSelectedClaimLine] = useState(null);
  const [banking, setBanking] = useState([]);
  const [manualSubmit, setManualSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [liabilityModalShow, setLiabilityModalShow] = useState(false);
  // Invoice deletion + receipt reversal modals — hosted locally so the
  // "Reverse receipt" hand-off from the deletion modal stays in this
  // component's state (no cross-page routing).
  const [showInvoiceDeletionModal, setShowInvoiceDeletionModal] = useState(false);
  const [showReceiptReversalModal, setShowReceiptReversalModal] = useState(false);
  const [claim, setClaim] = useState({
    status: "Pending Submission",
    transactionNumber: "",
    message: "",
    financials: {
      submittedAmount: "",
      approvedGross: "",
      approvedNett: "",
      discount: "",
      surcharges: "",
      copay: "",
      levy: "",
      overCharged: "",
      feeOverCharged: "",
      calculatedGross: "",
    },
    patientClaimFinancials: {
      claimFinancialDescription: "",
      claimFinancialAmount: "",
    },
    overAllWarnings: {
      code: "",
      description: "",
    },
    overAllRejections: {
      code: "",
      description: "",
    },
    overAllFundWarnings: {
      code: "",
      description: "",
    },
    overAllFundRejections: {
      code: "",
      description: "",
    },
    hnetNumber: "",
    mediKreditMsg: "",
    lineResults: [
      {
        lineNumber: "",
        status: "",
        warnings: "",
        rejections: "",
        tariffCode: "",
        grossAmount: "",
      },
    ],
    accountId: "",
    invoiceId: "",
    practiceId: "",
    patientId: "",
  });

  const { id } = useParams(); // Get the invoiceId from the URL parameters
  const location = useLocation();

  const canAccessAccounts = useMemo(() => {
    try {
      const token = sessionStorage.getItem("user");
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role)
          ? user.role
          : user?.role
            ? [user.role]
            : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some((r) => ["SuperAdmin", "Finance"].includes(r));
    } catch {
      return false;
    }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!invoice?._id || !account?._id) return null;
    const fi = (account.accountFullNames || "").charAt(0);
    const accountLabel =
      `Account - ${fi ? fi + " " : ""}${account.accountLastName || ""}`.trim() ||
      "Account";
    const invoiceLabel = `Invoice ${invoice.invoiceNumber}`;

    // Generic trail from source page (per D-06)
    const incomingTrail = location.state?.breadcrumbTrail;
    if (incomingTrail?.length) {
      return [...incomingTrail, { label: invoiceLabel }];
    }

    // Canonical fallback (per D-14) — same as existing default
    return [
      { label: "Accounts", path: "/billing", canAccess: canAccessAccounts },
      {
        label: accountLabel,
        path: `/accounts/${account._id}`,
        canAccess: canAccessAccounts,
      },
      { label: invoiceLabel },
    ];
  }, [invoice, account, location.state, canAccessAccounts]);

  useEffect(() => {
    const getInvoiceData = async () => {
      try {
        const token = sessionStorage.getItem("user");
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken._id);

        // Wave 1: invoice + claim — both keyed by URL `id`, independent of each other.
        const [response, claimData] = await Promise.all([
          getInvoiceById(id),
          getClaimByInvoiceId(id),
        ]);
        setInvoice(response);

        // Wave 2: account / patient / practice / practitioner — all derived from
        // the invoice document, independent of each other.
        const [accountData, patientData, practiceData, practitionerData] =
          await Promise.all([
            getAccountById(response.accountId),
            getPatientById(response.patient.patientId),
            getPracticeById(response.practice.practiceNumber),
            getUserById(response.practitioner.id),
          ]);
        setAccount(accountData);
        setPatients(patientData);
        setPractice(practiceData);
        setPractitioner(practitionerData);

        if (claimData) {
          setClaim(claimData);
          setClaimId(claimData._id);
        }
      } catch (error) {
        console.error("Error fetching invoices:", error);
      }
    };
    getInvoiceData();
  }, [id]);

  useEffect(() => {
    if (claim?._id) {
      setClaimId(claim?._id);
      console.log("claim updated:", claim);
    }
  }, [claim]);

  async function handlePrint() {
    if (invoice) {
      // Create base invoice data object. Older invoices' patient snapshots
      // predate patientDOB being captured — fill it from the fetched patient
      // doc so the PDF can render "(01) Name (DOB: yyyy-mm-dd)".
      const invoiceData = {
        ...invoice,
        account,
        practice,
        practitioner,
        patient: invoice?.patient
          ? {
              ...invoice.patient,
              patientDOB: invoice.patient.patientDOB || patient?.patientDOB || "",
            }
          : invoice?.patient,
      };

      try {
        // Get raw banking data
        const rawBankingData = await getBankingDetailsByPracticeNumbers([
          invoiceData.practice.practiceNumber,
        ]);

        // Ensure banking data exists before attempting to decrypt
        if (rawBankingData && rawBankingData.length > 0) {
          // Decrypt banking data
          const decryptedBankingData = await Promise.all(
            rawBankingData.map(async (entry) => {
              const decryptedAccountNumber = entry.bank?.accountNumber
                ? await decryptField(entry.bank.accountNumber)
                : "";
              const decryptedAccountName = entry.bank?.accountName
                ? await decryptField(entry.bank.accountName)
                : "";

              return {
                ...entry,
                bank: {
                  ...entry.bank,
                  accountNumber: decryptedAccountNumber,
                  accountName: decryptedAccountName,
                },
              };
            }),
          );

          // Set banking data in state for component use
          setBanking(decryptedBankingData);

          // Important: Directly assign the decrypted banking data to the invoiceData
          // before exporting to PDF
          invoiceData.banking = decryptedBankingData;
        } else {
          console.warn(
            "No banking details found for practice number:",
            invoiceData.practice.practiceNumber,
          );
          invoiceData.banking = [];
        }

        // Export the PDF with the complete data including decrypted banking details
        await ExportPDFInvoice(invoiceData);
        console.log("exportInvoiceData", invoiceData);
      } catch (error) {
        console.error("Error in handlePrint:", error);
        // Still try to export the PDF even if banking details failed
        await ExportPDFInvoice(invoiceData);
      }
    } else {
      console.warn("No invoice data available for printing");
    }
  }

  async function prepareEmailInvoice() {
    try {
      if (invoice) {
        // Same patientDOB backfill as handlePrint — see comment there.
        const invoiceData = {
          ...invoice,
          account,
          practice,
          practitioner,
          patient: invoice?.patient
            ? {
                ...invoice.patient,
                patientDOB: invoice.patient.patientDOB || patient?.patientDOB || "",
              }
            : invoice?.patient,
        };
        const rawBankingData = await getBankingDetailsByPracticeNumbers([
          invoiceData.practice.practiceNumber,
        ]);

        // Ensure banking data exists before attempting to decrypt
        if (rawBankingData && rawBankingData.length > 0) {
          // Decrypt banking data
          const decryptedBankingData = await Promise.all(
            rawBankingData.map(async (entry) => {
              const decryptedAccountNumber = entry.bank?.accountNumber
                ? await decryptField(entry.bank.accountNumber)
                : "";
              const decryptedAccountName = entry.bank?.accountName
                ? await decryptField(entry.bank.accountName)
                : "";

              return {
                ...entry,
                bank: {
                  ...entry.bank,
                  accountNumber: decryptedAccountNumber,
                  accountName: decryptedAccountName,
                },
              };
            }),
          );

          // Set banking data in state for component use
          setBanking(decryptedBankingData);

          // Important: Directly assign the decrypted banking data to the invoiceData
          // before exporting to PDF
          invoiceData.banking = decryptedBankingData;
        } else {
          console.warn(
            "No banking details found for practice number:",
            invoiceData.practice.practiceNumber,
          );
          invoiceData.banking = [];
        }

        // Call ExportPDFInvoice with asBlob: true directly
        const blob = await ExportPDFInvoice(invoiceData, { asBlob: true });

        // Only continue if we got a blob back
        if (blob) {
          setStatementBlob(blob);
          setShowEmailModal(true);
        }
      }
    } catch (error) {
      console.error("❌ Failed to generate invoice PDF:", error);
    }
  }

  const handleSubmitClaim = async () => {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);

    try {
      console.log("Claim submission response:", invoice);
      console.log("Account data at submission:", account);
      const response = await SubmitClaim({
        invoice,
        account,
        practice,
        selectedPatient: patient,
        claimtxn: "",
      });
      console.log("Claim submission response:", invoice);
      const lineStatuses = (response.lineResults || []).map((l) =>
        (l?.status || "").toUpperCase(),
      );

      let computedStatus;
      if (lineStatuses.length > 0) {
        if (lineStatuses.every((s) => s === "R")) {
          computedStatus = "Switch Rejected";
        } else if (lineStatuses.some((s) => s === "R" || s === "W")) {
          const mediKreditStatus = (
            response.mediKreditStatus || ""
          ).toLowerCase();
          if (
            mediKreditStatus === "p" &&
            !response.overAllRejections &&
            !response.overAllFundRejections
          ) {
            computedStatus = "Processed - Item Level Warnings Or Rejections";
          } else {
            computedStatus = "Rejected";
          }
          if (mediKreditStatus === "p") {
            computedStatus = "Processed";
          }
          setMessage("Item Level Warnings Or Rejections");
        } else {
          computedStatus = "Processed";
        }
      } else {
        // fallback if no lineResults
        const code = (response.mediKreditStatus || "").toLowerCase();
        computedStatus =
          code === "p"
            ? "Processed"
            : code === "r"
              ? "Switch Rejected"
              : code === "w"
                ? "Item Level Warnings Or Rejections"
                : "Pending";
      }

      const updatedClaim = {
        ...response,
        status: computedStatus,
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        createdBy: userId,
        createdDate: new Date().toISOString(),
      };
      setClaim(updatedClaim);
      const claimCreated = await createClaim(updatedClaim);

      const newInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Processed")
          ? "Posted - Claim Processed"
          : "Posted - Claim in Progress",
      };
      setInvoice(newInvoice);
      await updateInvoice(newInvoice?._id, newInvoice);
      setClaimId(claimCreated.claimId);
      navigate(0);
    } catch (err) {
      console.error("🚨 Error in claim submission:", err);
      const errorMsg =
        err.response?.data?.error || err.message || "Unknown error";
      setErrorMessage(errorMsg);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  };

  const handleReceipt = () => {
    setShowReceiptModal(true);
  };

  const handleUpdateClaim = () => {
    navigate(`/invoices/${invoice?._id}/${account?._id}`, {
      // state: { initialInvoiceApp: invoice }, // ✅ pass full invoice
    });
  };

  async function handleUpdateInvoice() {
    navigate(`/invoices/${invoice?._id}/${account?._id}`, {});
  }

  async function handleResubmitClaim() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    setClaimId(claim?._id);
    try {
      const response = await SubmitClaim({
        invoice,
        account,
        practice,
        selectedPatient: patient,
        claimtxn: claim.transactionNumber,
      });

      const lineStatuses = (response.lineResults || []).map((l) =>
        (l.status || "").toUpperCase(),
      );

      let computedStatus;
      if (lineStatuses.length > 0) {
        if (lineStatuses.every((s) => s === "R")) {
          computedStatus = "Switch Rejected";
        } else if (lineStatuses.some((s) => s === "R" || s === "W")) {
          const mkStatus = (response.mediKreditStatus || "").toLowerCase();
          computedStatus =
            mkStatus === "p"
              ? "Processed - Item Level Warnings Or Rejections"
              : "Processed";
          setMessage("Item Level Warnings Or Rejections");
        } else {
          computedStatus = "Processed";
        }
      } else {
        const code = (response.mediKreditStatus || "").toLowerCase();
        computedStatus =
          code === "p"
            ? "Processed"
            : code === "r"
              ? "Switch Rejected"
              : code === "w"
                ? "Item Level Warnings Or Rejections"
                : "Pending";
      }

      const updatedClaim = {
        ...response,
        _id: claimId,
        status: computedStatus,
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Resubmitted",
        userId: userId,
      };

      // Save claim
      setClaim(updatedClaim);
      await updateClaim(updatedClaim?._id, updatedClaim);

      const updatedInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Processed")
          ? "Posted - Claim Processed"
          : "Posted - Claim in Progress",
      };

      setInvoice(updatedInvoice);
      await updateInvoice(updatedInvoice?._id, updatedInvoice);
    } catch (err) {
      console.error("🚨 Error resubmitting claim:", err);
      const errorMsg =
        err.response?.data?.error || err.message || "Unknown error";
      setErrorMessage(errorMsg);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleReverseClaim() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    try {
      // Build a reset invoice where every non-private line moves back onto the
      // medical aid liability. The Medikredit switch validates the reversal
      // total against the original claim total, so any portion previously
      // pushed to the patient (after partial/rejected approval, or via Change
      // Liability) must move back before we send the reversal — otherwise the
      // switch rejects the call.
      const round2 = (n) => Number((parseFloat(n ?? 0) || 0).toFixed(2));
      const cleanNumber = (v) => round2(String(v ?? 0).replace(/[, ]+/g, ""));

      const resetLines = (invoice?.lines || []).map((line) => {
        if (line.privatePayment) return line;
        const full = cleanNumber(line.rate);
        return { ...line, patientAmount: 0, medAidAmount: full };
      });

      const totals = resetLines.reduce(
        (acc, l) => {
          acc.patient = round2(acc.patient + (l.patientAmount || 0));
          acc.medAid = round2(acc.medAid + (l.medAidAmount || 0));
          return acc;
        },
        { patient: 0, medAid: 0 },
      );

      const sumPaymentsFor = (bucket) =>
        (invoice?.receipts || []).reduce((sum, r) => {
          const src = String(r.paymentSource || "")
            .toLowerCase()
            .replace(/\s+/g, "");
          const match =
            bucket === "patient" ? src === "patient" : src === "medicalaid";
          if (!match) return sum;
          const receiptAmt = cleanNumber(r.receiptAmount);
          const creditAmt = cleanNumber(r.creditNoteAmount);
          const refundAmt = cleanNumber(r.refundAmount);
          return round2(sum + receiptAmt + creditAmt - refundAmt);
        }, 0);

      const patientReceipts = sumPaymentsFor("patient");
      const medAidReceipts = sumPaymentsFor("medicalAid");
      const patientOutstanding = round2(totals.patient - patientReceipts);
      const medAidOutstanding = round2(totals.medAid - medAidReceipts);

      const resetInvoice = {
        ...invoice,
        lines: resetLines,
        patientTotal: totals.patient,
        totalPatientAmount: totals.patient,
        medAidTotal: totals.medAid,
        totalMedicalAidAmount: totals.medAid,
        outstandingBalance: {
          ...(invoice?.outstandingBalance || {}),
          patientOutstanding,
          medAidOutstanding,
          totalOutstanding: round2(patientOutstanding + medAidOutstanding),
          paidAmount: round2(patientReceipts + medAidReceipts),
        },
      };

      const reverseClaim = await ReverseClaim({
        account,
        invoice: resetInvoice,
        practice: practice,
        selectedPatient: patient,
        claimtxn: claim?.transactionNumber,
      });

      // Determine status based on MK status code
      const mediKreditStatus = (status) => {
        switch (status?.toLowerCase()) {
          case "p":
            return "Processed - Claim Reversed";
          case "r":
            return "Reversal Switch Rejected";
          case "w":
            return "Processed - Item Level Warnings Or Rejections";
          case "a":
            return "Approved - Claim Reversed";
          default:
            return "Pending";
        }
      };

      const mkCode = String(reverseClaim?.mediKreditStatus || "").toLowerCase();
      // "r" = the switch rejected the reversal itself. The ORIGINAL claim is
      // still live at Medikredit, so we must not wipe lineResults or demote
      // the invoice status — doing so silently reclassified the approved nett
      // amounts as patient liability (line-split logic further down treats a
      // claim with empty lineResults and no "Switch Rejected" status as having
      // no scheme approval).
      const reversalRejected = mkCode === "r";

      // Update local claim object — preserve the prior lineResults when the
      // reversal was rejected so downstream allocation still sees the scheme's
      // approved amounts.
      const updatedClaimData = {
        ...reverseClaim,
        _id: claimId,
        lineResults: reversalRejected ? claim?.lineResults || [] : [],
        status: mediKreditStatus(reverseClaim?.mediKreditStatus),
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Reversed",
        userId: userId,
      };
      setClaim(updatedClaimData);

      // Only move the invoice back to "Posted - Claim in Progress" when the
      // reversal was actually accepted. Using the full "Posted - " prefix
      // matches SubmitClaim's post-submit value and is required by the
      // backend's determineInvoiceStatus — any other string falls through to
      // plain "Posted" on the next receipt/credit-note update, which also
      // contributed to the liability shift.
      if (!reversalRejected) {
        // Persist the reset (all-medical-aid) liability split alongside the
        // status update, so the invoice's stored line/totals match what the
        // switch just accepted.
        const updatedInvoiceData = {
          ...resetInvoice,
          status: "Posted - Claim in Progress",
        };
        setInvoice(updatedInvoiceData);
        await updateInvoice(invoice?._id, updatedInvoiceData);
      }

      const updatedClaim = await updateClaim(claimId, updatedClaimData);
      setClaimId(updatedClaim.claimId);

      if (reversalRejected) {
        setErrorMessage(
          "The reversal was rejected by the switch. The original claim is still active at Medikredit — please retry or contact support.",
        );
        setIsServiceUnavailable(true);
      }
    } catch (err) {
      console.error("🚨 Error in claim update:", err);
      const errorMsg =
        err.response?.data?.error || err.message || "Unknown error";
      setErrorMessage(errorMsg);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleChangeLiability() {
    setLiabilityModalShow(true);
  }

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "draft":
        return "warning";
      case "pending":
        return "secondary";
      case "update posted":
        return "danger";
      case "posted":
        return "success";
      case "claim - processed":
        return "success";
      case "posted - claim in progress":
        return "secondary";
      case "paid":
        return "primary";
      case "claim in progress":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "dark";
    }
  };

  const getStatusText = (code) => {
    switch (code) {
      case "R":
        return "Rejected";
      case "P":
        return "Processed";
      case "W":
        return "Processed with Warnings";
      case "A":
        return "Approved";
      default:
        return code || "-";
    }
  };

  const getStatusVariantLines = (code, approvedAmount) => {
    // Rejected line OR zero approved → red. Everything else (Processed,
    // Processed with Warnings, Approved, …) renders in primary so the line
    // cards stay visually uniform unless something actually went wrong.
    if (
      code === "R" ||
      approvedAmount == null ||
      approvedAmount === 0 ||
      approvedAmount === "0.00"
    ) {
      return "danger";
    }
    return "primary";
  };

  async function handleManualClaim() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    try {
      const updatedClaim = {
        status: "Manual Submission",
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        createdBy: userId,
        createdDate: new Date().toISOString(),
      };
      setClaim(updatedClaim);
      const claimCreated = await createClaim(updatedClaim);

      const newInvoice = {
        ...invoice,
        status: "Posted - Manual Submission",
      };
      setInvoice(newInvoice);
      await updateInvoice(invoice?._id, newInvoice);
      setClaimId(claimCreated.claimId);
      setManualSubmit(true);
    } catch (err) {
      console.error("🚨 Error in claim submission:", err);
      setIsServiceUnavailable(true);
    } finally {
      setLoadingClaim(false);
    }
  }

  async function handleManualResubmit() {
    setLoadingClaim(true);
    setIsServiceUnavailable(false);
    setClaimId(claim?._id);
    try {
      const updatedClaim = {
        _id: claimId,
        status: "Manual Resubmission",
        accountId: account?._id,
        invoiceId: invoice?._id,
        practiceId: practice?._id,
        patientId: patient?._id,
        action: "Resubmitted",
        userId: userId,
      };
      setClaim(updatedClaim);
      await updateClaim(updatedClaim?._id, updatedClaim);

      const updatedInvoice = {
        ...invoice,
        status: updatedClaim?.status.includes("Posted")
          ? "Posted - Manual Submission"
          : "Posted - Manual Submission",
      };
      setManualSubmit(true);
      setInvoice(updatedInvoice);
      await updateInvoice(updatedInvoice?._id, updatedInvoice);
    } catch (err) {
      console.error("🚨 Error resubmitting claim:", err);
    } finally {
      setLoadingClaim(false);
    }
  }

  // Reversal window. Medikredit attaches a `reversal` value (days) to the
  // claim at submit/reverse time via the medikredit_list lookup. We use that
  // plus claim.createdAt to compute whether the window is still open and how
  // much time is left. No live ticking — computed once per render; close-and-
  // reopen the page to refresh. Memoised so a render-triggering parent state
  // change doesn't churn the calc.
  const reversalState = useMemo(() => {
    const reversedStatuses = [
      "Approved - Claim Reversed",
      "Processed - Claim Reversed",
    ];
    if (reversedStatuses.includes(claim?.status)) return { hide: true };
    const days = Number(claim?.reversal);
    if (!Number.isFinite(days) || days <= 0) return { hide: true };
    if (!claim?.createdAt) return { hide: true };
    const submittedAt = new Date(claim.createdAt);
    if (Number.isNaN(submittedAt.getTime())) return { hide: true };
    const expiresAt = new Date(
      submittedAt.getTime() + days * 24 * 60 * 60 * 1000,
    );
    const msLeft = expiresAt.getTime() - Date.now();
    if (msLeft <= 0) return { hide: false, expired: true };
    const hoursLeft = Math.floor(msLeft / (60 * 60 * 1000));
    const daysLeft = Math.floor(hoursLeft / 24);
    let remainingText;
    if (daysLeft >= 1) {
      remainingText = `${daysLeft} day${daysLeft === 1 ? "" : "s"}`;
    } else {
      const hrs = Math.max(1, hoursLeft);
      remainingText = `${hrs} hour${hrs === 1 ? "" : "s"}`;
    }
    return { hide: false, expired: false, remainingText };
  }, [claim?.reversal, claim?.createdAt, claim?.status]);
  const reversalExpired = reversalState.expired === true;

  const STATUS_BUTTONS = {
    "Pending Submission": [
      { label: "Update Claim", onClick: handleUpdateClaim, icon: "fa-pen" },
      {
        label: "E-Submission",
        onClick: handleSubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Manual Submission",
        onClick: handleManualClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    Processed: [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
        disabled: reversalExpired,
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Processed - Not Paid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Processed - Item Level Warnings Or Rejections": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
        disabled: reversalExpired,
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Switch Rejected": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Manual Submission": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Manual Resubmission": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Approved - Claim Reversed": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Processed - Claim Reversed": [
      {
        label: "Update Claim",
        onClick: handleUpdateClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Claim Updated": [
      {
        label: "E-Resubmission",
        onClick: handleResubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Manual Resubmission",
        onClick: handleManualResubmit,
        icon: "fa-file-invoice-dollar",
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Rejected - Medical Aid": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
        disabled: reversalExpired,
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Resubmit Claim",
        onClick: handleResubmitClaim,
        icon: "fa-file-invoice-dollar",
      },
    ],
    "Processed - Medical Aid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    Finalised: [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
    ],
    "Finalised - Not Paid": [
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    "Reversal Switch Rejected": [
      {
        label: "Reverse Claim",
        onClick: handleReverseClaim,
        icon: "fa-rotate-left",
        disabled: reversalExpired,
      },
      {
        label: "Receive Payment",
        onClick: handleReceipt,
        icon: "fa-cash-register",
      },
      {
        label: "Change Liability",
        onClick: handleChangeLiability,
        icon: "fa-scale-balanced",
      },
    ],
    Paid: [{}],
  };

  const buttons = STATUS_BUTTONS[claim?.status] || [];

  const buttonRows = [];
  for (let i = 0; i < buttons.length; i += 2) {
    buttonRows.push([buttons[i], buttons[i + 1]]);
  }

  // Place these outside the return/render block
  async function calculateAndSaveLineAmounts(invoice, claim) {
    // Guard
    if (!invoice || !Array.isArray(invoice.lines)) {
      return { patientTotal: 0, medAidTotal: 0 };
    }
    if (!claim._id) {
      return {
        patientTotal: invoice.patientTotal,
        medAidTotal: invoice.medAidTotal,
      };
    }

    const round2 = (n) => Number((parseFloat(n ?? 0) || 0).toFixed(2));
    const cleanNumber = (v) =>
      round2(
        String(v ?? 0)
          .toString()
          .replace(/[, ]+/g, ""),
      );

    // Step 1: update each line with patientAmount / medAidAmount
    // IOD accounts are treated the same as medical aid — amounts go to scheme, not patient
    const hasScheme = Boolean(
      invoice?.medicalAid?.name || account?.accountIODFund,
    );

    invoice.lines = invoice.lines.map((line) => {
      // Discount lines short-circuit the claim-split logic. Their negative
      // patientAmount / medAidAmount were already loaded by CreateInvoice
      // onto the chosen target — preserve them as-is so totals net out.
      if (line.type === "discount") {
        return {
          ...line,
          patientAmount: round2(line.patientAmount || 0),
          medAidAmount: round2(line.medAidAmount || 0),
        };
      }

      const full = cleanNumber(line.rate);
      const claimLine = claim?.lineResults?.find(
        (c) => String(c.lineNumber) === String(line.lineNumber),
      );

      let medAidAmt = 0;
      let patientAmt = 0;

      if (!hasScheme || line.privatePayment) {
        // No scheme on account, or explicitly private → patient pays full
        patientAmt = full;
      } else if (claim.status === "Switch Rejected") {
        // Entire claim was rejected by switch — no scheme approval, patient pays full
        patientAmt = full;
      } else if (!claimLine) {
        // Scheme present but no claim feedback yet
        // If there is a billable code (tariff or nappi), allocate to scheme
        const hasBillableCode = Boolean(line.tariffCode || line.nappiCode);
        medAidAmt = hasBillableCode ? full : 0;
        patientAmt = hasBillableCode ? 0 : full;
      } else {
        // We have claim feedback: split by approved nett
        const approved = cleanNumber(claimLine.approvedNettAmount);
        // Clamp to avoid weird negatives/over-allocations
        medAidAmt = Math.max(0, Math.min(approved, full));
        patientAmt = round2(full - medAidAmt);
      }
      console.log(
        "medAidAmt",
        medAidAmt,
        "patientAmt",
        patientAmt,
        "for line",
        line.lineNumber,
      );

      return {
        ...line,
        patientAmount: round2(patientAmt),
        medAidAmount: round2(medAidAmt),
      };
    });
    // Step 2: totals
    const totals = invoice.lines.reduce(
      (acc, line) => {
        acc.patientTotal = round2(acc.patientTotal + (line.patientAmount || 0));
        acc.medAidTotal = round2(acc.medAidTotal + (line.medAidAmount || 0));
        return acc;
      },
      { patientTotal: 0, medAidTotal: 0 },
    );
    console.log("Calculated totals from lines:", totals);

    // Step 3: receipts / credit-notes / refunds per bucket
    const sumPaymentsFor = (bucket) =>
      (invoice.receipts || []).reduce((sum, r) => {
        const src = String(r.paymentSource || "")
          .toLowerCase()
          .replace(/\s+/g, ""); // "medical aid" → "medicalaid"
        const match =
          bucket === "patient" ? src === "patient" : src === "medicalaid";
        if (!match) return sum;

        const receiptAmt = cleanNumber(r.receiptAmount);
        const creditAmt = cleanNumber(r.creditNoteAmount);
        const refundAmt = cleanNumber(r.refundAmount);

        // receipts + credits reduce outstanding; refunds increase it
        return round2(sum + receiptAmt + creditAmt - refundAmt);
      }, 0);

    const patientReceipts = sumPaymentsFor("patient");
    const medAidReceipts = sumPaymentsFor("medicalAid");

    // Step 4: outstanding & paid
    const patientOutstanding = round2(totals.patientTotal - patientReceipts);
    const medAidOutstanding = round2(totals.medAidTotal - medAidReceipts);
    const totalOutstanding = round2(patientOutstanding + medAidOutstanding);
    const paidAmount = round2(patientReceipts + medAidReceipts);

    // Step 5: write back to invoice
    invoice.patientTotal = totals.patientTotal;
    invoice.totalPatientAmount = totals.patientTotal;
    invoice.medAidTotal = totals.medAidTotal;
    invoice.totalMedicalAidAmount = totals.medAidTotal;
    invoice.outstandingBalance = {
      patientOutstanding,
      medAidOutstanding,
      totalOutstanding,
      paidAmount,
    };
    console.log("Updated invoice with totals and outstanding:", invoice);
    await updateInvoice(invoice?._id, invoice);
    console.log("Totals from invoice state:", {
      patientTotal: totals.patientTotal,
      medAidTotal: totals.medAidTotal,
    });
    return {
      patientTotal: totals.patientTotal,
      medAidTotal: totals.medAidTotal,
    };
  }

  useEffect(() => {
    // Once a claim is Finalised (any variant), the invoice's stored split is
    // authoritative — receipts, ERA allocation and liability shifts own it.
    // Re-deriving from claim.lineResults here would restore the ORIGINAL
    // approved amounts (manual receipts never restamp them), silently moving
    // patient-shifted outstanding back into the medical-aid column on every
    // page open.
    const claimFinalised = String(claim?.status || "").startsWith("Finalised");
    if (invoice?._id && claim?.lineResults?.length > 0 && !claimFinalised) {
      calculateAndSaveLineAmounts(invoice, claim)
        .then(({ patientTotal, medAidTotal }) => {
          setPatTotal(patientTotal);
          setMedTotal(medAidTotal);
        })
        .catch((err) => {
          console.error("🚨 Failed to update invoice:", err);
        });
    } else if (invoice?._id) {
      setPatTotal(invoice.patientTotal ?? invoice.totalPatientAmount ?? 0);
      setMedTotal(invoice.medAidTotal ?? invoice.totalMedicalAidAmount ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, claim, account?.accountIODFund]);

  useEffect(() => {
    if (invoice?._id && Array.isArray(invoice.receipts)) {
      // initialize all six totals
      let totalPatientReceipts = 0;
      let totalMedicalAidReceipts = 0;
      let totalPatientCreditNotes = 0;
      let totalMedicalAidCreditNotes = 0;
      let totalPatientRefunds = 0;
      let totalMedicalAidRefunds = 0;

      invoice.receipts.forEach((r) => {
        // normalize source key to lower-case for consistency
        const src = (r.paymentSource || "").toString().toLowerCase();

        // 1) Receipts
        if (r.receiptAmount != null) {
          const amt = parseFloat(r.receiptAmount) || 0;
          if (src === "patient") totalPatientReceipts += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidReceipts += amt;
        }

        // 2) Credit-notes
        if (r.creditNoteAmount != null) {
          const amt = parseFloat(r.creditNoteAmount) || 0;
          if (src === "patient") totalPatientCreditNotes += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidCreditNotes += amt;
        }

        // 3) Refunds
        if (r.refundAmount != null) {
          const amt = parseFloat(r.refundAmount) || 0;
          if (src === "patient") totalPatientRefunds += amt;
          else if (src === "medicalaid" || src === "medical aid")
            totalMedicalAidRefunds += amt;
        }
      });

      // set them all into state
      setPatReceiptTotal(totalPatientReceipts);
      setMedReceiptTotal(totalMedicalAidReceipts);
      setPatCreditTotal(totalPatientCreditNotes);
      setMedCreditTotal(totalMedicalAidCreditNotes);
      setPatRefundTotal(totalPatientRefunds);
      setMedRefundTotal(totalMedicalAidRefunds);
    }
  }, [invoice]);

  const F = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00"; // fallback

    return num.toFixed(2); // ALWAYS uses "." regardless of locale
  };

  // Breakdown of the per-target totals into gross + discount so the totals
  // card can render the same 3-line stack as CreateInvoice:
  //   Medical Aid          R 1000.00
  //   Medical Aid Discount   (200.00)
  //   Total Medical Aid     R 800.00
  // Discount lines carry their negative split on patientAmount/medAidAmount
  // and a positive `discountAmount` + `discountTarget` we use to bucket the
  // displayed discount per side.
  const lineDiscountBreakdown = useMemo(() => {
    const lines = Array.isArray(invoice?.lines) ? invoice.lines : [];
    let medAidDiscount = 0;
    let patientDiscount = 0;
    for (const l of lines) {
      if (l?.type !== "discount") continue;
      const amt = Math.abs(parseFloat(l.discountAmount ?? l.rate ?? 0) || 0);
      if (l.discountTarget === "medicalAid") {
        medAidDiscount += amt;
      } else {
        patientDiscount += amt;
      }
    }
    return {
      medAidDiscount: Number(medAidDiscount.toFixed(2)),
      patientDiscount: Number(patientDiscount.toFixed(2)),
    };
  }, [invoice?.lines]);

  const grossMedTotal = Number(
    (Number(medTotal || 0) + lineDiscountBreakdown.medAidDiscount).toFixed(2),
  );
  const grossPatTotal = Number(
    (Number(patTotal || 0) + lineDiscountBreakdown.patientDiscount).toFixed(2),
  );

  const ICD10Display = ({ oti, primaryCode, secondaryCodes = [] }) => {
    // Function to chunk array into groups of 2
    const chunkArray = (array, chunkSize) => {
      const chunks = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
      }
      return chunks;
    };

    const secondaryChunks = chunkArray(secondaryCodes, 2);

    return (
      <div className="text-center">
        {/* Primary ICD10 Code */}
        <div className="fw-semibold text-center mb-1 d-flex justify-content-center align-items-center gap-2">
          {oti === "PB" && (
            <span className="badge bg-success text-white small px-2 py-1">
              PMB
            </span>
          )}
          <span>{primaryCode}</span>
        </div>

        {/* Secondary ICD10 Codes */}
        {secondaryChunks.map((chunk, index) => (
          <div key={index} className="text-center mb-1">
            {chunk.map((code, codeIndex) => (
              <span key={codeIndex} className="me-1">
                {code}
              </span>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // Compact appointment date/time — date-first, no leading zeros.
  //   Same date:      "2-7-2026 | 8:30-9:00"
  //   No end value:   "2-7-2026 | 8:30"
  //   Different date: "2-7-2026 8:30 - 3-7-2026 9:00" (single-line contexts)
  const apptDate = (iso) => {
    const d = new Date(iso);
    if (!iso || isNaN(d)) return "";
    return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
  };
  const apptTime = (iso) => {
    const d = new Date(iso);
    if (!iso || isNaN(d)) return "";
    return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  const apptRange = (startStr, endStr) => {
    if (!startStr) return "";
    const sd = apptDate(startStr);
    const st = apptTime(startStr);
    if (!endStr) return `${sd} | ${st}`;
    const ed = apptDate(endStr);
    const et = apptTime(endStr);
    if (sd === ed) return `${sd} | ${st}-${et}`;
    return `${sd} ${st} - ${ed} ${et}`;
  };

  // Single-line label for discount rows and mobile cards.
  const apptDateLabel = (line) => {
    const start = line.appointmentCustomStart || line.appointmentStartSet || "";
    const end = line.appointmentCustomEnd || line.appointmentEndSet || "";
    const fallback =
      line.appointmentDateCustom || line.appointmentDateSet || "-";
    if (!start) return fallback;
    if (isLineDateOnly(line)) {
      const sd = apptDate(start);
      const ed = end ? apptDate(end) : "";
      return ed && ed !== sd ? `${sd} - ${ed}` : sd;
    }
    return apptRange(start, end);
  };

  // Whether the bottom status row should show the "Claim:" badge — true when
  // there's any scheme attached (invoice-level or account-level) to claim
  // against. Mirrors the gating on the Claim Status accordion below.
  const hasClaim = !!(
    invoice?.medicalAid?.name ||
    account?.accountMedicalAid ||
    account?.accountIODFund
  );

  // Actions menu items — rendered inside both the mobile rail and the desktop
  // rail Dropdowns so the same logic stays in one place. The Dropdown.Menu
  // wrapper around this JSX differs per rail (alignment / drop direction).
  const actionsMenuItems = (
    <>
      {/* Paid → Only Update Invoice */}
      {invoice?.status === "Paid" && (
        <Dropdown.Item onClick={handleUpdateInvoice}>
          <i className="fa fa-edit me-2"></i>
          Update Invoice
        </Dropdown.Item>
      )}

      {/* Posted → Receive Payment + Update Invoice + (optional) Change Liability */}
      {invoice?.status === "Posted" && (
        <>
          <Dropdown.Item onClick={handleReceipt}>
            <i className="fa fa-receipt me-2"></i>
            Receive Payment
          </Dropdown.Item>
          <Dropdown.Item onClick={handleUpdateInvoice}>
            <i className="fa fa-edit me-2"></i>
            Update Invoice
          </Dropdown.Item>
          {/* Change Liability — show whenever the account now has a
              scheme to shift liability to. We intentionally don't
              gate on line.privatePayment: pre-scheme invoices were
              never flagged private (the flag only fires when a
              line is explicitly marked private despite an existing
              scheme), so gating on it would hide the action for
              exactly the historical-private case it's meant to
              handle. */}
          {(account?.accountMedicalAid || account?.accountIODFund) && (
            <Dropdown.Item onClick={handleChangeLiability}>
              <i className="fa fa-scale-balanced me-2"></i>
              Change Liability
            </Dropdown.Item>
          )}
        </>
      )}

      {/* Partially Paid → Update Invoice (discount-only edit). The edit
          screen locks every line-changing action for this status, so only a
          discount can be added against the remaining outstanding — a
          discount that zeroes the balance flips the invoice to "Paid". Safe
          regardless of claim status: the claimable lines cannot change and
          the claim is not reset on save. */}
      {invoice?.status === "Partially Paid" && (
        <Dropdown.Item onClick={handleUpdateInvoice}>
          <i className="fa fa-edit me-2"></i>
          Update Invoice
        </Dropdown.Item>
      )}

      {/* Other statuses → Claim actions */}
      {invoice?.status !== "Paid" &&
        invoice?.status !== "Posted" &&
        STATUS_BUTTONS[claim?.status]?.length > 0 && (
          <>
            {STATUS_BUTTONS[claim?.status].map(
              (button, index) =>
                button.label && (
                  <Dropdown.Item
                    key={index}
                    onClick={button.onClick}
                    disabled={!!button.disabled}
                    className={button.disabled ? "text-muted" : ""}
                  >
                    <i className={`fa ${button.icon} me-2`}></i>
                    {button.label}
                  </Dropdown.Item>
                ),
            )}
          </>
        )}

      {/* Delete invoice — available for every status. The modal handles
          its own block rules (active receipt, scheme-paid claim) and
          surfaces the receipt-reversal hand-off when blocked. */}
      <Dropdown.Divider />
      <Dropdown.Item
        onClick={() => setShowInvoiceDeletionModal(true)}
        className="text-danger"
      >
        <i className="fa-solid fa-trash me-2" />
        Delete invoice
      </Dropdown.Item>
    </>
  );

  return (
    <div>
      {manualSubmit && (
        <Modal show={manualSubmit} onHide={() => setManualSubmit(false)}>
          <Modal.Header>
            <Modal.Title className="text-primary">
              Manual Submission
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              You have marked this claim as manually submitted. There will be no
              action from PractoSync, and you will need to follow up with the
              medical aid directly.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setManualSubmit(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      <div style={{ marginTop: "50px" }}>
        <Container fluid className="px-0">
          <Row className="g-0">
            {/* Mobile rail strip — horizontal-scroll icon row, visible <lg */}
            <Col xs={12} className="d-flex d-lg-none g-0">
              <Container className="bg-white g-0 shadow-sm rounded">
                <div className="acc-rail-mobile">
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      as={Button}
                      className="acc-rail-item acc-rail-item-labeled"
                      id="actions-dropdown-mobile"
                      aria-label="Actions"
                      title="Actions"
                    >
                      <i className="fa-solid fa-gears" />
                      <span className="acc-rail-label">Actions</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      className="shadow-sm actions-dropdown"
                      renderOnMount
                      popperConfig={{
                        strategy: "fixed",
                        modifiers: [
                          {
                            name: "computeStyles",
                            options: {
                              adaptive: false,
                              gpuAcceleration: false,
                            },
                          },
                        ],
                      }}
                    >
                      {actionsMenuItems}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button
                    className="acc-rail-item acc-rail-item-labeled"
                    aria-label="Email invoice"
                    title="Email"
                    onClick={prepareEmailInvoice}
                  >
                    <i className="fa-regular fa-envelope" />
                    <span className="acc-rail-label">Email</span>
                  </Button>
                  <Button
                    className="acc-rail-item acc-rail-item-labeled"
                    aria-label="Download invoice"
                    title="Download"
                    onClick={handlePrint}
                  >
                    <i className="fa fa-download" />
                    <span className="acc-rail-label">Download</span>
                  </Button>
                </div>
              </Container>
            </Col>

            {/* Desktop vertical rail — 64px sticky icon column, visible lg+.
              Container is min 100vh so the white background fills the
              viewport even when the content next to it is short. */}
            <Col
              xs="auto"
              className="d-none d-lg-block pt-0 g-0"
              style={{ width: 64 }}
            >
              <Container
                className="bg-white g-0 p-1 shadow-sm rounded"
                style={{ width: 64, minHeight: "100vh" }}
              >
                <div
                  className="acc-rail"
                  style={{ position: "sticky", top: 55 }}
                >
                  <Dropdown drop="end">
                    <Dropdown.Toggle
                      as={Button}
                      className="acc-rail-item acc-rail-item-labeled"
                      id="actions-dropdown-desktop"
                      aria-label="Actions"
                      title="Actions"
                    >
                      <i className="fa-solid fa-gears" />
                      <span className="acc-rail-label">Actions</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      className="shadow-sm actions-dropdown"
                      renderOnMount
                      popperConfig={{
                        strategy: "fixed",
                        modifiers: [
                          {
                            name: "computeStyles",
                            options: {
                              adaptive: false,
                              gpuAcceleration: false,
                            },
                          },
                        ],
                      }}
                    >
                      {actionsMenuItems}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button
                    className="acc-rail-item acc-rail-item-labeled"
                    aria-label="Email invoice"
                    title="Email"
                    onClick={prepareEmailInvoice}
                  >
                    <i className="fa-regular fa-envelope" />
                    <span className="acc-rail-label">Email</span>
                  </Button>
                  <Button
                    className="acc-rail-item acc-rail-item-labeled"
                    aria-label="Download invoice"
                    title="Download"
                    onClick={handlePrint}
                  >
                    <i className="fa fa-download" />
                    <span className="acc-rail-label">Download</span>
                  </Button>
                </div>
              </Container>
            </Col>

            {/* Main content column — fills remaining width next to the rail.
              The outer wrapper has `.acc-list` for CSS-scope only (brand
              tokens cascade in); `.acc-list-bare` strips the visual card
              surface so the breadcrumb / heading / status badges read as
              sitting above the inner cards, not inside one. */}
            <Col xs={12} md className="g-0">
              <div className="acc-list acc-list-bare ms-2 me-2 mb-3 pt-3">
                {breadcrumbItems && (
                  <div className="ms-1 mb-2">
                    <Breadcrumb items={breadcrumbItems} />
                  </div>
                )}
                {/* Header row — title on the left, invoice# + status badges on
                  the right. On narrow widths the badges wrap below the title
                  (still inside the same flex row). */}
                <div className="d-flex align-items-center flex-wrap gap-2 mb-3 ms-1 me-1 justify-content-between">
                  <h4 className="text-primary fw-normal mb-0">
                    Invoice &amp; Claim
                  </h4>
                  <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
                    {/* Invoice number — desktop only */}
                    {invoice?.invoiceNumber && (
                      <span
                        className="d-none d-lg-inline-block badge rounded-pill bg-light text-primary border py-2 px-3"
                        style={{ fontSize: "0.7rem" }}
                      >
                        #{invoice.invoiceNumber}
                      </span>
                    )}
                    {/* Invoice status — always visible (mobile + desktop) */}
                    {invoice?.status && (
                      <span
                        className={`badge rounded-pill bg-${getStatusVariant(
                          invoice.status,
                        )} py-2 px-3`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        Invoice: {invoice.status}
                      </span>
                    )}
                    {/* Claim status — desktop only */}
                    {hasClaim && claim?.status && (
                      <span
                        className={`d-none d-lg-inline-block badge rounded-pill bg-${getStatusVariant(
                          claim.status,
                        )} py-2 px-3`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        Claim: {claim.status}
                      </span>
                    )}
                  </div>
                </div>

                <Row className="g-3">
                  {/* Left: Invoice header + lines (renders below sidebar on
                    mobile, left of sidebar on desktop via order-* utilities) */}
                  <Col xs={12} lg={9} className="order-2 order-lg-1">
                    {/* Unified posted-invoice card — meta / line items / totals as
              three sections separated by faint horizontal rules. Sits flush
              with the top of the Col so it aligns horizontally with the
              sidebar accordion. */}
                    <>
                      {/* Meta block — 4-up responsive grid (1col mobile, 2col tablet,
                4col desktop) with light blue tint. Section titles inline
                replace the old gradient header row. */}
                      <div className="posted-invoice-meta p-3 rounded mb-3">
                        <Row className="g-3">
                          {/* --- Account --- */}
                          <Col xs={12} md={6} lg={3} className="small">
                            <div className="posted-meta-section-title">
                              Account
                            </div>
                            <p className="fw-semibold text-primary mb-1">
                              {account?.accountTitle}{" "}
                              {account?.accountFullNames}{" "}
                              {account?.accountLastName}
                            </p>
                            <p className="mb-0">
                              {account?.accountHomeAddress1}
                            </p>
                            <p className="mb-0">
                              {account?.accountHomeAddress2}
                            </p>
                            <p className="mb-0">
                              {account?.accountHomeAddressCity}{" "}
                              {account?.accountHomeAddressPostalCode}
                            </p>
                            <p className="mb-0">
                              {account?.accountTelNumberCell}
                            </p>
                            <p className="mb-0">{account?.accountEmail}</p>
                          </Col>

                          {/* --- Patient --- */}
                          <Col xs={12} md={6} lg={3} className="small">
                            <div className="posted-meta-section-title">
                              Patient
                            </div>
                            <p className="fw-semibold text-primary mb-1">
                              {patient?.patientTitle}{" "}
                              {patient?.patientFullNames} (
                              {patient?.patientInitials}){" "}
                              {patient?.patientLastName}
                            </p>
                            {account.accountMedicalAid ? (
                              <>
                                <p className="mb-0 d-flex align-items-center gap-2">
                                  <span className="badge bg-primary">
                                    Medical Aid
                                  </span>
                                  <span>{account.accountMedicalAid}</span>
                                </p>

                                {(account.accountMedicalAidPlan ||
                                  account.accountMedicalAidOption) && (
                                  <p className="mb-0">
                                    {account.accountMedicalAidPlan}
                                    {account.accountMedicalAidPlan &&
                                      account.accountMedicalAidOption &&
                                      " | "}
                                    {account.accountMedicalAidOption}
                                  </p>
                                )}

                                <p className="mb-0">
                                  {account.accountMedicalAidNumber}
                                  {patient?.patientMedicalAidDependantCode
                                    ? ` (${patient.patientMedicalAidDependantCode})`
                                    : ""}
                                </p>

                                {patient?.patientMedicalAidStatus && (
                                  <p
                                    className={`fw-bold mb-0 ${
                                      ["Suspended", "Resigned"].includes(
                                        patient.patientMedicalAidStatus,
                                      )
                                        ? "text-warning"
                                        : "text-success"
                                    }`}
                                  >
                                    {patient.patientMedicalAidStatus}
                                  </p>
                                )}
                              </>
                            ) : account.accountIODFund ? (
                              <>
                                <p className="mb-0 d-flex align-items-center gap-2">
                                  {(
                                    account.accountIODFundCategory || ""
                                  ).toUpperCase() === "INSURANCE" ? (
                                    <span className="badge bg-info text-dark">
                                      INS
                                    </span>
                                  ) : (
                                    <span className="badge bg-warning text-dark">
                                      IOD
                                    </span>
                                  )}
                                  <span>{account.accountIODFund}</span>
                                </p>

                                {(account.accountIODPlan ||
                                  account.accountIODOption) && (
                                  <p className="mb-0">
                                    {account.accountIODPlan}
                                    {account.accountIODPlan &&
                                      account.accountIODOption &&
                                      " | "}
                                    {account.accountIODOption}
                                  </p>
                                )}

                                {account.accountIODClaimRefNumber && (
                                  <p className="mb-0">
                                    Claim Ref:{" "}
                                    {account.accountIODClaimRefNumber}
                                  </p>
                                )}

                                {account.accountIODEmployer && (
                                  <p className="mb-0">
                                    Employer: {account.accountIODEmployer}
                                  </p>
                                )}

                                {account.accountIODEmployerWCARegNum && (
                                  <p className="mb-0">
                                    WCA Reg #:{" "}
                                    {account.accountIODEmployerWCARegNum}
                                  </p>
                                )}
                                {account.accountIODDateOfInjury && (
                                  <p className="mb-0">
                                    Date of Injury:{" "}
                                    {account.accountIODDateOfInjury}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="mb-0">
                                <span className="badge bg-secondary">
                                  Private Patient
                                </span>
                              </p>
                            )}
                          </Col>

                          {/* --- Practice & Practitioner --- */}
                          <Col xs={12} md={6} lg={3} className="small">
                            <div className="posted-meta-section-title">
                              Practice &amp; Practitioner
                            </div>
                            <p className="fw-semibold text-primary mb-1 text-uppercase">
                              {invoice?.practice?.practiceName}
                            </p>
                            <p className="mb-0">
                              BHF: {invoice?.practice?.practiceNumber}
                            </p>
                            <p className="mb-0">
                              VAT: {invoice?.practice?.vatNumber || "-"}
                            </p>
                            <p className="mb-0 text-uppercase mt-1">
                              {invoice?.practitioner?.firstName}{" "}
                              {invoice?.practitioner?.lastName}
                            </p>
                            <p className="mb-0">
                              HPCSA: {invoice?.practitioner?.hpcsa}
                            </p>
                          </Col>

                          {/* --- Invoice Info --- */}
                          <Col xs={12} md={6} lg={3} className="small">
                            <div className="posted-meta-section-title">
                              Invoice Info
                            </div>
                            <Row className="mb-1 gx-0">
                              <Col
                                xs={4}
                                className="fw-semibold text-primary text-start"
                              >
                                Invoice:
                              </Col>
                              <Col xs={8} className="text-start">
                                {invoice?.invoiceNumber}
                              </Col>
                            </Row>
                            <Row className="mb-1 gx-0">
                              <Col
                                xs={4}
                                className="fw-semibold text-primary text-start"
                              >
                                Invoice Date:
                              </Col>
                              <Col xs={8} className="text-start">
                                {invoice?.invoiceDate}
                              </Col>
                            </Row>
                            <Row className="mb-1 gx-0">
                              <Col
                                xs={4}
                                className="fw-semibold text-primary text-start"
                              >
                                Status:
                              </Col>
                              <Col xs={8} className="text-start">
                                {invoice?.status}
                              </Col>
                            </Row>
                          </Col>
                        </Row>

                        <hr className="posted-card-divider" />

                        {/* Desktop (lg+): line items table — header row + line rows.
                  No own wrapper; sits inside the unified posted-invoice card. */}
                        <div className="d-none d-lg-block posted-lines">
                          <Row className="mb-2 gx-2">
                            <Col
                              md={2}
                              className="posted-meta-section-title text-center"
                            >
                              Service Date
                            </Col>
                            <Col
                              md={1}
                              className="posted-meta-section-title text-center"
                            >
                              ICD10
                            </Col>
                            <Col
                              md={1}
                              className="posted-meta-section-title text-center"
                            >
                              Code
                            </Col>
                            <Col
                              md={4}
                              className="posted-meta-section-title text-center"
                            >
                              Description
                            </Col>
                            <Col
                              md={1}
                              className="posted-meta-section-title text-center"
                            >
                              Unit(s)
                            </Col>
                            <Col
                              md={1}
                              className="posted-meta-section-title text-center"
                            >
                              Patient
                            </Col>
                            <Col
                              md={1}
                              className="posted-meta-section-title text-center"
                            >
                              Med Aid
                            </Col>
                            <Col
                              md={1}
                              className="posted-meta-section-title text-center"
                            >
                              Total
                            </Col>
                          </Row>

                          {/* --- Line Items --- */}

                          {invoice?.lines?.map((line, i) => {
                            // Discount line — simple read-only row with the
                            // negative amount on the targeted side. Empty
                            // ICD / Code / Unit / Total cells to keep grid
                            // alignment with normal rows; negative amounts
                            // render in parentheses.
                            if (line.type === "discount") {
                              const amt = Math.abs(
                                parseFloat(
                                  line.discountAmount ?? line.rate ?? 0,
                                ) || 0,
                              );
                              const isMedAid =
                                line.discountTarget === "medicalAid";
                              const dateLabel = apptDateLabel(line);
                              return (
                                <Row
                                  key={`d-${i}`}
                                  className="mb-1 ms-1 border-bottom border-gray me-2 small"
                                >
                                  <Col md={2} className="text-center">
                                    {dateLabel}
                                  </Col>
                                  <Col md={1} className="text-center">
                                    -
                                  </Col>
                                  <Col md={1} className="text-center">
                                    -
                                  </Col>
                                  <Col md={4} className="text-start">
                                    {line.description || "Discount"}
                                  </Col>
                                  <Col md={1} className="text-center">
                                    -
                                  </Col>
                                  <Col md={1} className="text-end">
                                    {!isMedAid && amt > 0
                                      ? `(${F(amt)})`
                                      : `R ${F(0)}`}
                                  </Col>
                                  <Col md={1} className="text-end">
                                    {isMedAid && amt > 0
                                      ? `(${F(amt)})`
                                      : `R ${F(0)}`}
                                  </Col>
                                  <Col
                                    md={1}
                                    className="text-end fw-semibold text-primary"
                                  >
                                    {amt > 0
                                      ? `(${F(amt)})`
                                      : `R ${F(0)}`}
                                  </Col>
                                </Row>
                              );
                            }

                            const claimLine = claim?.lineResults?.find(
                              (c) =>
                                String(c.lineNumber) ===
                                String(line.lineNumber),
                            );

                            const full = parseFloat(line.rate) || 0;
                            const hasScheme = !!(
                              invoice.medicalAid?.name ||
                              account?.accountIODFund
                            );

                            let medAidAmt = 0;
                            let patientAmt = 0;
                            const totalAmt = full;

                            if (!hasScheme || line.privatePayment) {
                              patientAmt = full;
                            } else if (claim?.status === "Switch Rejected") {
                              // Entire claim rejected by switch — no scheme approval, patient pays full
                              patientAmt = full;
                            } else if (hasScheme && !claimLine) {
                              if (line.tariffCode || line.nappiCode) {
                                medAidAmt = full;
                              } else {
                                patientAmt = full;
                              }
                            } else {
                              const approved = parseFloat(
                                claimLine?.approvedNettAmount ?? 0,
                              );
                              medAidAmt = approved;
                              if (approved !== full) {
                                patientAmt = full - approved;
                              }
                            }
                            if (!claimLine && line.patientAmount) {
                              patientAmt = parseFloat(line.patientAmount) || 0;
                              medAidAmt = full - patientAmt;
                            }

                            return (
                              <React.Fragment key={i}>
                                {claimLine && (
                                  <>
                                    <Row className="mb-1 ms-1 me-2 align-items-center small">
                                      <Col
                                        md={3}
                                        className={`fw-bold text-start text-${getStatusVariantLines(
                                          claimLine?.status,
                                          claimLine?.approvedNettAmount,
                                        )}`}
                                      >
                                        {getStatusText(claimLine.status)}
                                      </Col>

                                      <Col
                                        md={4}
                                        className={`fw-bold text-end text-${getStatusVariantLines(
                                          claimLine?.status,
                                          claimLine?.approvedNettAmount,
                                        )}`}
                                      ></Col>

                                      <Col
                                        md={4}
                                        className={`fw-bold text-end text-${getStatusVariantLines(
                                          claimLine?.status,
                                          claimLine?.approvedNettAmount,
                                        )}`}
                                      >
                                        <div className="d-flex align-items-center justify-content-end">
                                          <span className="me-2">
                                            Approved Nett Amount:
                                          </span>
                                          <span>
                                            {claimLine?.approvedNettAmount !=
                                            null
                                              ? `R ${F(Number(claimLine?.approvedNettAmount))}`
                                              : "R 0.00"}
                                          </span>
                                        </div>
                                      </Col>
                                      <Col md={1} className="fw-bold text-end">
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 ms-2"
                                          onClick={() => {
                                            setSelectedClaimLine(claimLine);
                                            setShowLineInfo(true);
                                          }}
                                        >
                                          <i
                                            className={`fa fa-info-circle text-${getStatusVariantLines(
                                              claimLine?.status,
                                              claimLine?.approvedNettAmount,
                                            )}`}
                                          ></i>
                                        </Button>
                                      </Col>
                                    </Row>
                                  </>
                                )}

                                <Row className="mb-1 ms-1 border-bottom border-gray me-2 small">
                                  <Col md={2} className="text-center">
                                    {(() => {
                                      const start =
                                        line.appointmentCustomStart ||
                                        line.appointmentStartSet ||
                                        "";
                                      const end =
                                        line.appointmentCustomEnd ||
                                        line.appointmentEndSet ||
                                        "";
                                      const fallback =
                                        line.appointmentDateCustom ||
                                        line.appointmentDateSet ||
                                        "-";
                                      if (!start) return <div>{fallback}</div>;

                                      const sameDay =
                                        !end ||
                                        apptDate(start) === apptDate(end);

                                      // Date-only: one line, or two when the
                                      // dates span more than a single day.
                                      if (isLineDateOnly(line)) {
                                        return sameDay ? (
                                          <div>{apptDate(start)}</div>
                                        ) : (
                                          <>
                                            <div>{apptDate(start)}</div>
                                            <div>{apptDate(end)}</div>
                                          </>
                                        );
                                      }

                                      // Timed same day → single compact line.
                                      if (sameDay) {
                                        return (
                                          <div>{apptRange(start, end)}</div>
                                        );
                                      }

                                      // Timed across days → keep on two lines.
                                      return (
                                        <>
                                          <div>{`${apptDate(start)} ${apptTime(start)}`}</div>
                                          <div>{`${apptDate(end)} ${apptTime(end)}`}</div>
                                        </>
                                      );
                                    })()}
                                  </Col>

                                  <Col md={1} className="text-center">
                                    <ICD10Display
                                      oti={line.oti}
                                      primaryCode={line.icd101}
                                      secondaryCodes={
                                        line.icd102
                                          ? line.icd102
                                              .split(";")
                                              .map((code) => code.trim())
                                              .filter(Boolean)
                                          : []
                                      }
                                    />
                                  </Col>
                                  <Col md={1} className="text-center">
                                    {(() => {
                                      // If it's a NAPPI line (either explicitly typed or has a code), show nappiCode.
                                      // A linked NAPPI shows its tariff code stacked above the NAPPI code.
                                      const isNappi =
                                        line.type === "nappi" ||
                                        !!line.nappiCode;
                                      if (isNappi) {
                                        if (!line.nappiCode) return "-";
                                        return line.linkedTariffCode ? (
                                          <div className="small lh-sm">
                                            <div className="fw-semibold">
                                              {line.linkedTariffCode}
                                            </div>
                                            <div>{line.nappiCode}</div>
                                          </div>
                                        ) : (
                                          line.nappiCode
                                        );
                                      }

                                      // Otherwise show tariff with modifiers inline.
                                      const mods = Array.isArray(line.modifier)
                                        ? line.modifier
                                            .map((m) =>
                                              typeof m === "string"
                                                ? m
                                                : m?.code,
                                            )
                                            .filter(Boolean)
                                        : [];

                                      if (!line.tariffCode) return "-";
                                      return mods.length
                                        ? `${line.tariffCode} (${mods.join(", ")})`
                                        : line.tariffCode;
                                    })()}
                                  </Col>

                                  <Col md={4} className="text-start">
                                    <span
                                      title={(() => {
                                        const desc = line.description || "";
                                        const spName = line.serviceProvider
                                          ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`
                                          : invoice?.practitioner
                                            ? `${invoice.practitioner.firstName} ${invoice.practitioner.lastName}`
                                            : "";
                                        return spName
                                          ? `${desc}\nService Provider: ${spName}`
                                          : desc;
                                      })()}
                                      style={{ cursor: "help" }}
                                    >
                                      {line.description || "-"}
                                    </span>
                                  </Col>
                                  <Col md={1} className="text-center">
                                    {line.quantity || "-"}
                                  </Col>
                                  <Col md={1} className="text-end">
                                    R {F(patientAmt)}
                                  </Col>
                                  <Col md={1} className="text-end">
                                    R {F(medAidAmt)}
                                  </Col>
                                  <Col
                                    md={1}
                                    className="text-end fw-semibold text-primary"
                                  >
                                    R {(totalAmt ?? 0).toFixed(2)}
                                  </Col>
                                </Row>
                              </React.Fragment>
                            );
                          })}
                        </div>

                        {/* Mobile (<lg): card-per-line — compact summary; full claim
                detail still opens via the existing line-info Modal. */}
                        <div className="d-lg-none">
                          {invoice?.lines?.map((line, i) => {
                            // Discount line — compact mobile card with the
                            // negative amount on the top row and the target
                            // labelled in the body.
                            if (line.type === "discount") {
                              const amt = Math.abs(
                                parseFloat(
                                  line.discountAmount ?? line.rate ?? 0,
                                ) || 0,
                              );
                              const isMedAid =
                                line.discountTarget === "medicalAid";
                              const dateLabel = apptDateLabel(line);
                              return (
                                <div
                                  className="acc-card posted-line-card"
                                  key={`m-d-${i}`}
                                >
                                  <div className="acc-card-top">
                                    <span className="acc-card-id">
                                      Discount · {dateLabel}
                                    </span>
                                    <span className="acc-card-amount acc-list-num">
                                      ({F(amt)})
                                    </span>
                                  </div>
                                  <div className="acc-card-body">
                                    <div className="acc-card-field">
                                      <span className="acc-card-label">
                                        Description
                                      </span>
                                      <span className="acc-card-value">
                                        {line.description || "Discount"}
                                      </span>
                                    </div>
                                    <div className="acc-card-field">
                                      <span className="acc-card-label">
                                        Applied to
                                      </span>
                                      <span className="acc-card-value">
                                        {isMedAid ? "Medical Aid" : "Patient"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            }

                            const claimLine = claim?.lineResults?.find(
                              (c) =>
                                String(c.lineNumber) ===
                                String(line.lineNumber),
                            );
                            const full = parseFloat(line.rate) || 0;
                            const hasScheme = !!(
                              invoice.medicalAid?.name ||
                              account?.accountIODFund
                            );
                            let medAidAmt = 0;
                            let patientAmt = 0;
                            if (!hasScheme || line.privatePayment) {
                              patientAmt = full;
                            } else if (claim?.status === "Switch Rejected") {
                              patientAmt = full;
                            } else if (hasScheme && !claimLine) {
                              if (line.tariffCode || line.nappiCode)
                                medAidAmt = full;
                              else patientAmt = full;
                            } else {
                              const approved = parseFloat(
                                claimLine?.approvedNettAmount ?? 0,
                              );
                              medAidAmt = approved;
                              if (approved !== full)
                                patientAmt = full - approved;
                            }
                            if (!claimLine && line.patientAmount) {
                              patientAmt = parseFloat(line.patientAmount) || 0;
                              medAidAmt = full - patientAmt;
                            }
                            const dateLabel = apptDateLabel(line);
                            const isNappi =
                              line.type === "nappi" || !!line.nappiCode;
                            const mods = Array.isArray(line.modifier)
                              ? line.modifier
                                  .map((m) =>
                                    typeof m === "string" ? m : m?.code,
                                  )
                                  .filter(Boolean)
                              : [];
                            const codeLabel = isNappi
                              ? line.nappiCode
                                ? line.linkedTariffCode
                                  ? `${line.linkedTariffCode} · ${line.nappiCode}`
                                  : line.nappiCode
                                : "-"
                              : line.tariffCode
                                ? mods.length
                                  ? `${line.tariffCode} (${mods.join(", ")})`
                                  : line.tariffCode
                                : "-";
                            return (
                              <div
                                className="acc-card posted-line-card"
                                key={`m-${i}`}
                              >
                                <div className="acc-card-top">
                                  <span className="acc-card-id">
                                    {codeLabel} · {dateLabel}
                                  </span>
                                  <span className="acc-card-amount acc-list-num">
                                    R {(full ?? 0).toFixed(2)}
                                  </span>
                                </div>
                                <div className="acc-card-body">
                                  <div className="acc-card-field">
                                    <span className="acc-card-label">
                                      Description
                                    </span>
                                    <span className="acc-card-value">
                                      {line.description || "-"}
                                    </span>
                                  </div>
                                  {(line.icd101 || line.icd102) && (
                                    <div className="acc-card-field">
                                      <span className="acc-card-label">
                                        ICD-10
                                      </span>
                                      <span className="acc-card-value">
                                        <ICD10Display
                                          oti={line.oti}
                                          primaryCode={line.icd101}
                                          secondaryCodes={
                                            line.icd102
                                              ? line.icd102
                                                  .split(";")
                                                  .map((c) => c.trim())
                                                  .filter(Boolean)
                                              : []
                                          }
                                        />
                                      </span>
                                    </div>
                                  )}
                                  <div className="acc-card-field">
                                    <span className="acc-card-label">
                                      Units
                                    </span>
                                    <span className="acc-card-value">
                                      {line.quantity || "-"}
                                    </span>
                                  </div>
                                  <div className="acc-card-field">
                                    <span className="acc-card-label">
                                      Patient
                                    </span>
                                    <span className="acc-card-value acc-list-num">
                                      R {F(patientAmt)}
                                    </span>
                                  </div>
                                  <div className="acc-card-field">
                                    <span className="acc-card-label">
                                      Med Aid
                                    </span>
                                    <span className="acc-card-value acc-list-num">
                                      R {F(medAidAmt)}
                                    </span>
                                  </div>
                                  {claimLine && (
                                    <div className="acc-card-field">
                                      <span
                                        className={`posted-line-status text-${getStatusVariantLines(
                                          claimLine?.status,
                                          claimLine?.approvedNettAmount,
                                        )}`}
                                      >
                                        {getStatusText(claimLine.status)}
                                      </span>
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={() => {
                                          setSelectedClaimLine(claimLine);
                                          setShowLineInfo(true);
                                        }}
                                      >
                                        <i
                                          className={`fa fa-info-circle text-${getStatusVariantLines(
                                            claimLine?.status,
                                            claimLine?.approvedNettAmount,
                                          )} me-1`}
                                        />
                                        Claim detail
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <Modal
                          show={showLineInfo}
                          onHide={() => setShowLineInfo(false)}
                          size="lg"
                          centered
                        >
                          <Modal.Header closeButton>
                            <Modal.Title>Line details</Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <Row className="mb-1 me-2">
                              <p className="mb-0 fw-bold text-primary">
                                Line Number: {selectedClaimLine?.lineNumber}
                              </p>
                            </Row>

                            <p className="mb-0 text-primary border-bottom border-primary pb-2">
                              {selectedClaimLine?.tariffCode} -{" "}
                              {selectedClaimLine?.description}
                            </p>

                            <Row className="mb-1 me-2 mt-2 align-items-center">
                              <Col
                                md={4}
                                className={`fw-bold text-start text-${getStatusVariantLines(
                                  selectedClaimLine?.status,
                                )}`}
                              >
                                <h6>
                                  {getStatusText(selectedClaimLine?.status)}
                                </h6>
                              </Col>
                            </Row>

                            <Col
                              md={5}
                              className={`text-start text-${getStatusVariantLines(
                                selectedClaimLine?.status,
                              )}`}
                            >
                              {selectedClaimLine?.warnings?.map(
                                (warn, index) => (
                                  <p className="mb-0" key={`warn-${index}`}>
                                    {warn.code} - {warn.description}
                                  </p>
                                ),
                              )}

                              {selectedClaimLine?.rejections?.map(
                                (rej, index) => (
                                  <p className="mb-0" key={`rej-${index}`}>
                                    {rej.code} - {rej.description}
                                  </p>
                                ),
                              )}
                            </Col>

                            <Col md={3}></Col>

                            <hr />

                            <dl className="row">
                              <dt className="col-6">Submitted Amount</dt>
                              <dd className="col-6 text-end fw-bold">
                                {selectedClaimLine?.submittedGrossAmount
                                  ? `R ${Number(
                                      selectedClaimLine.submittedGrossAmount,
                                    ).toFixed(2)}`
                                  : "R 0.00"}
                              </dd>
                              <dt className="col-6">Approved Gross</dt>
                              <dd className="col-6 text-end fw-bold">
                                {selectedClaimLine?.approvedGrossAmount
                                  ? `R ${Number(
                                      selectedClaimLine.approvedGrossAmount,
                                    ).toFixed(2)}`
                                  : "R 0.00"}
                              </dd>
                              <dt className="col-6 fw-normal">
                                Approved Discount
                              </dt>
                              <dd className="col-6 text-end">
                                {selectedClaimLine?.approvedDiscount
                                  ? `R ${Number(
                                      selectedClaimLine.approvedDiscount,
                                    ).toFixed(2)}`
                                  : "R 0.00"}
                              </dd>
                              <dt className="col-6 fw-normal">
                                Approved Copay
                              </dt>
                              <dd className="col-6 text-end">
                                {selectedClaimLine?.approvedCopay
                                  ? `R ${Number(
                                      selectedClaimLine.approvedCopay,
                                    ).toFixed(2)}`
                                  : "R 0.00"}
                              </dd>
                              <dt className="col-6 fw-normal">Approved Levy</dt>
                              <dd className="col-6 text-end">
                                {selectedClaimLine?.approvedLevy
                                  ? `R ${Number(
                                      selectedClaimLine.approvedLevy,
                                    ).toFixed(2)}`
                                  : "R 0.00"}
                              </dd>
                              <dt className="col-6">Approved Net</dt>
                              <dd className="col-6 text-end border-top fw-bold">
                                {selectedClaimLine?.approvedNettAmount
                                  ? `R ${Number(
                                      selectedClaimLine.approvedNettAmount,
                                    ).toFixed(2)}`
                                  : "R 0.00"}
                              </dd>
                              <dt className="col-6 fw-bold">Over-charged</dt>
                              <dd className="col-6 text-end fw-bold">
                                {selectedClaimLine?.overCharged
                                  ? `R ${Number(
                                      selectedClaimLine.overCharged,
                                    ).toFixed(2)}`
                                  : "R 0.00"}
                              </dd>
                            </dl>

                            {/* ⬇️ Modifier Details */}
                            {Array.isArray(selectedClaimLine?.modifiers) &&
                              selectedClaimLine.modifiers.length > 0 && (
                                <>
                                  <hr />
                                  {selectedClaimLine.modifiers.map(
                                    (mod, idx) => (
                                      <div
                                        key={`mod-${idx}`}
                                        className="ms-3 mb-3"
                                      >
                                        <h6 className="text-primary">
                                          Modifier:{" "}
                                          {mod.modifierCode ||
                                            mod.modifier ||
                                            "Unknown"}
                                        </h6>
                                        <p
                                          className={`ms-2 mb-1 fw-bold text-${getStatusVariantLines(
                                            mod.status,
                                          )}`}
                                        >
                                          Status: {getStatusText(mod.status)}
                                        </p>

                                        {mod.warnings?.length > 0 && (
                                          <div>
                                            {mod.warnings.map((warn, wIdx) => (
                                              <p
                                                className="ms-2 mb-0 text-secondary"
                                                key={`mod-warn-${wIdx}`}
                                              >
                                                {warn.code} - {warn.description}
                                              </p>
                                            ))}
                                          </div>
                                        )}

                                        {mod.rejections?.length > 0 && (
                                          <div>
                                            {mod.rejections.map((rej, rIdx) => (
                                              <p
                                                className="ms-2 mb-0 text-danger"
                                                key={`mod-rej-${rIdx}`}
                                              >
                                                {rej.code} - {rej.description}
                                              </p>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ),
                                  )}
                                </>
                              )}
                          </Modal.Body>

                          <Modal.Footer>
                            <Button
                              variant="secondary"
                              onClick={() => setShowLineInfo(false)}
                            >
                              Close
                            </Button>
                          </Modal.Footer>
                        </Modal>

                        {/* Totals — section-title labels + semi-bold primary values.
                  Sits inside the unified posted-invoice card as its 3rd
                  section, directly under the last line item with no divider
                  in between. */}
                        <Row className="mx-0">
                          <Col xs={12} lg={{ span: 5, offset: 7 }}>
                            <div className="acc-list-totals">
                              {lineDiscountBreakdown.medAidDiscount > 0 && (
                                <>
                                  <div className="d-flex justify-content-between align-items-center py-1">
                                    <span className="posted-meta-section-title mb-0 mt-3">
                                      Medical Aid
                                    </span>
                                    <span className="text-primary acc-list-num">
                                      R {F(grossMedTotal)}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center py-1">
                                    <span className="posted-meta-section-title mb-0">
                                      Medical Aid Discount
                                    </span>
                                    <span className="text-primary acc-list-num">
                                      ({F(lineDiscountBreakdown.medAidDiscount)})
                                    </span>
                                  </div>
                                </>
                              )}
                              <div
                                className="d-flex justify-content-between align-items-center py-1"
                                style={{
                                  borderBottom:
                                    "1px solid rgba(15, 52, 144, 0.10)",
                                }}
                              >
                                <span
                                  className={`posted-meta-section-title mb-0 ${
                                    lineDiscountBreakdown.medAidDiscount > 0
                                      ? ""
                                      : "mt-3"
                                  }`}
                                >
                                  Total Medical Aid
                                </span>
                                <span className="fw-semibold text-primary acc-list-num">
                                  R {F(medTotal)}
                                </span>
                              </div>
                              {lineDiscountBreakdown.patientDiscount > 0 && (
                                <>
                                  <div className="d-flex justify-content-between align-items-center py-1">
                                    <span className="posted-meta-section-title mb-0">
                                      Patient
                                    </span>
                                    <span className="text-primary acc-list-num">
                                      R {F(grossPatTotal)}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center py-1">
                                    <span className="posted-meta-section-title mb-0">
                                      Patient Discount
                                    </span>
                                    <span className="text-primary acc-list-num">
                                      ({F(lineDiscountBreakdown.patientDiscount)})
                                    </span>
                                  </div>
                                </>
                              )}
                              <div
                                className="d-flex justify-content-between align-items-center py-1"
                                style={{
                                  borderBottom:
                                    "1px solid rgba(15, 52, 144, 0.10)",
                                }}
                              >
                                <span className="posted-meta-section-title mb-0">
                                  Total Patient
                                </span>
                                <span className="fw-semibold text-primary acc-list-num">
                                  R {F(patTotal)}
                                </span>
                              </div>
                              {!!invoice?.practice?.vatNumber && (
                                <div className="d-flex justify-content-between align-items-center py-1">
                                  <span className="posted-meta-section-title mb-0">
                                    VAT (15%)
                                  </span>
                                  <span className="text-primary acc-list-num">
                                    R{" "}
                                    {F(
                                      Number(invoice?.totalAmount || 0) -
                                        Number(invoice?.totalAmount || 0) / 1.15,
                                    )}
                                  </span>
                                </div>
                              )}
                              <div
                                className="d-flex justify-content-between align-items-center py-1 mt-1"
                                style={{
                                  borderBottom:
                                    "2px solid rgba(15, 52, 144, 0.25)",
                                }}
                              >
                                <span className="posted-meta-section-title mb-0">
                                  Invoice Total
                                </span>
                                <span className="fw-semibold text-primary acc-list-num">
                                  R {F(invoice?.totalAmount)}
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>

                        {/* Footer — created date + creator, right-aligned. */}
                        <hr className="posted-card-divider" />
                        <div className="d-flex justify-content-end">
                          <div className="text-end small text-muted">
                            Created{" "}
                            {invoice?.invoiceCreatedDate
                              ? `${apptDate(invoice.invoiceCreatedDate)} ${apptTime(
                                  invoice.invoiceCreatedDate,
                                )}`
                              : "-"}
                            {invoice?.invoiceCreatedByName
                              ? ` by ${invoice.invoiceCreatedByName}`
                              : ""}
                          </div>
                        </div>
                      </div>
                    </>
                  </Col>
                  <Col xs={12} lg={3} className="order-1 order-lg-2">
                    <div className="acc-list-sidebar">
                      <Accordion className="mt-0">
                        {/* === RECEIPTS === */}
                        <Accordion.Item eventKey="0">
                          <Accordion.Header>
                            <Col
                              xs={4}
                              className="posted-meta-section-title mb-0 d-flex align-items-center"
                            >
                              <i className="fa-solid fa-money-bill-wave text-primary me-2"></i>
                              Due
                            </Col>
                            <Col xs={6} className="text-primary text-end">
                              R{" "}
                              {F(invoice?.outstandingBalance?.totalOutstanding)}
                            </Col>
                          </Accordion.Header>
                          <Accordion.Body>
                            {invoice?.receipts !== null ? (
                              invoice?.status === "Paid" ||
                              invoice?.status === "Partially Paid" ? (
                                <>
                                  <Row className="gx-0 mb-1">
                                    <Col
                                      xs={6}
                                      className="fw-normal text-primary"
                                    >
                                      Invoice Amount:
                                    </Col>
                                    <Col
                                      xs={6}
                                      className="text-primary text-end fw-normal"
                                    >
                                      R {F(invoice?.totalAmount)}
                                    </Col>
                                  </Row>

                                  {patReceiptTotal > 0 && (
                                    <Row className="gx-0 mb-1">
                                      <Col
                                        xs={6}
                                        className="fw-normal text-primary"
                                      >
                                        Paid by Patient:
                                      </Col>
                                      <Col
                                        xs={6}
                                        className="text-primary text-end fw-normal"
                                      >
                                        (R {F(patReceiptTotal)})
                                      </Col>
                                    </Row>
                                  )}
                                  {medReceiptTotal > 0 && (
                                    <Row className="gx-0 mb-1">
                                      <Col
                                        xs={6}
                                        className="fw-normal text-primary"
                                      >
                                        Paid by Medical Aid:
                                      </Col>
                                      <Col
                                        xs={6}
                                        className="text-primary text-end fw-normal"
                                      >
                                        (R {F(medReceiptTotal)})
                                      </Col>
                                    </Row>
                                  )}
                                  {patCreditTotal > 0 && (
                                    <Row className="gx-0 mb-1">
                                      <Col
                                        xs={6}
                                        className="fw-normal text-primary"
                                      >
                                        Credit Note to Patient:
                                      </Col>
                                      <Col
                                        xs={6}
                                        className="text-primary text-end"
                                      >
                                        (R {F(patCreditTotal)})
                                      </Col>
                                    </Row>
                                  )}
                                  {medCreditTotal > 0 && (
                                    <Row className="gx-0 mb-1">
                                      <Col
                                        xs={6}
                                        className="fw-normal text-primary"
                                      >
                                        Credit Note to Medical Aid:
                                      </Col>
                                      <Col
                                        xs={6}
                                        className="text-primary text-end"
                                      >
                                        (R {F(medCreditTotal)})
                                      </Col>
                                    </Row>
                                  )}
                                  {patRefundTotal > 0 && (
                                    <Row className="gx-0 mb-1">
                                      <Col
                                        xs={6}
                                        className="fw-normal text-primary"
                                      >
                                        Refund to Patient:
                                      </Col>
                                      <Col
                                        xs={6}
                                        className="text-primary text-end"
                                      >
                                        R {patRefundTotal.toFixed(2)}
                                      </Col>
                                    </Row>
                                  )}
                                  {medRefundTotal > 0 && (
                                    <Row className="gx-0 mb-1">
                                      <Col
                                        xs={6}
                                        className="fw-normal text-primary"
                                      >
                                        Refund to Medical Aid:
                                      </Col>
                                      <Col
                                        xs={6}
                                        className="text-primary text-end"
                                      >
                                        R {medRefundTotal.toFixed(2)}
                                      </Col>
                                    </Row>
                                  )}

                                  <Row className="gx-0 mb-1 mt-2 border-top border-2 border-primary mb-3">
                                    <Col
                                      xs={6}
                                      className="fw-bold text-primary"
                                    >
                                      Total Outstanding:
                                    </Col>
                                    <Col
                                      xs={6}
                                      className="text-primary text-end fw-bold"
                                    >
                                      R{" "}
                                      {F(
                                        invoice?.outstandingBalance
                                          ?.totalOutstanding,
                                      )}
                                    </Col>
                                  </Row>
                                </>
                              ) : (
                                <Row>
                                  <Col className="text-muted small fst-italic text-center">
                                    No paid / partial payments.
                                  </Col>
                                </Row>
                              )
                            ) : (
                              <Row>
                                <Col className="text-muted small fst-italic text-center">
                                  No receipts / credits / refunds.
                                </Col>
                              </Row>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>

                        {/* === CASE === */}
                        {(invoice?.case || invoice?.inPatientCase) &&
                          invoice?.medicalAid?.name && (
                            <Accordion.Item eventKey="1">
                              <Accordion.Header>
                                <Col
                                  xs={4}
                                  className="posted-meta-section-title mb-0 d-flex align-items-center"
                                >
                                  <i className="fa-solid fa-folder-open text-primary me-2"></i>
                                  Case
                                </Col>
                              </Accordion.Header>
                              <Accordion.Body>
                                {(() => {
                                  const cRaw = invoice?.case ?? {};
                                  const legacy = invoice?.inPatientCase ?? {};
                                  const c = {
                                    description:
                                      cRaw.description ??
                                      cRaw.caseDescription ??
                                      "—",
                                    referringDoctorName:
                                      cRaw.referringDoctorName ?? "—",
                                    referringDoctorBHF:
                                      cRaw.referringDoctorBHF ??
                                      legacy.caseBHF ??
                                      "—",
                                    referringDoctorHPCSA:
                                      cRaw.referringDoctorHPCSA ??
                                      legacy.caseHPCSA ??
                                      "—",
                                    authorizedBy: cRaw.authorizedBy ?? "—",
                                    authorizationNumber:
                                      cRaw.authorizationNumber ??
                                      legacy.caseAuthNumber ??
                                      "—",
                                    startDate:
                                      cRaw.startDate ??
                                      cRaw.caseStartDate ??
                                      "—",
                                    endDate:
                                      cRaw.endDate ?? cRaw.caseEndDate ?? "—",
                                    icd101:
                                      cRaw.icd101 ?? legacy.caseICD10 ?? "",
                                    icd102: Array.isArray(cRaw.icd102)
                                      ? cRaw.icd102
                                      : [],
                                  };
                                  const icdPrimary = c.icd101 || "—";
                                  const icdSecondary = c.icd102.length
                                    ? c.icd102.join(", ")
                                    : "—";

                                  return (
                                    <>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Description:
                                        </Col>
                                        <Col xs={6}>{c.description}</Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Doctor:
                                        </Col>
                                        <Col xs={6}>
                                          {c.referringDoctorName}
                                        </Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Ref BHF:
                                        </Col>
                                        <Col xs={6}>{c.referringDoctorBHF}</Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Ref HPCSA:
                                        </Col>
                                        <Col xs={6}>
                                          {c.referringDoctorHPCSA}
                                        </Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Authorized By:
                                        </Col>
                                        <Col xs={6}>{c.authorizedBy}</Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Authorization #:
                                        </Col>
                                        <Col xs={6}>
                                          {c.authorizationNumber}
                                        </Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Start Date:
                                        </Col>
                                        <Col xs={6}>{c.startDate}</Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          End Date:
                                        </Col>
                                        <Col xs={6}>{c.endDate}</Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          ICD-10 (Primary):
                                        </Col>
                                        <Col xs={6}>{icdPrimary}</Col>
                                      </Row>
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          ICD-10 (Secondary):
                                        </Col>
                                        <Col xs={6}>{icdSecondary}</Col>
                                      </Row>
                                    </>
                                  );
                                })()}
                              </Accordion.Body>
                            </Accordion.Item>
                          )}
                        {/* === CLAIM STATUS === */}
                        {/* Also gate on account-level scheme so historical pre-scheme
                invoices (where invoice.medicalAid was never populated) still
                surface the claim panel once a medical aid is added to the
                account and a claim has been created via Change Liability. */}
                        {(invoice?.medicalAid?.name ||
                          account?.accountMedicalAid ||
                          account?.accountIODFund) && (
                          <Accordion.Item eventKey="2">
                            <Accordion.Header>
                              <Col
                                xs={4}
                                className="posted-meta-section-title mb-0 d-flex align-items-center"
                              >
                                <i
                                  className={`fa-solid fa-file-medical me-2 ${
                                    isServiceUnavailable
                                      ? "text-danger"
                                      : "text-primary"
                                  }`}
                                ></i>
                                <span
                                  className={
                                    isServiceUnavailable ? "text-danger" : ""
                                  }
                                >
                                  Claim Status
                                </span>
                              </Col>

                              <Col xs={6} className="text-end">
                                {/* Show spinner while loading */}
                                {loadingClaim ? (
                                  <Spinner
                                    animation="border"
                                    role="status"
                                    size="sm"
                                    variant={
                                      isServiceUnavailable
                                        ? "danger"
                                        : "primary"
                                    }
                                    className="me-2"
                                  >
                                    <span className="visually-hidden">
                                      Loading…
                                    </span>
                                  </Spinner>
                                ) : (
                                  <span
                                    className={`${
                                      isServiceUnavailable
                                        ? "text-danger"
                                        : "text-primary"
                                    }`}
                                  >
                                    {claim?.status}
                                  </span>
                                )}
                              </Col>
                            </Accordion.Header>
                            <Accordion.Body>
                              <div id="claim-body">
                                {/* 1) Service unavailable? */}
                                {isServiceUnavailable ? (
                                  <Row className="mb-3">
                                    <Col>
                                      <Alert
                                        variant="danger"
                                        className="text-center mb-0"
                                      >
                                        {errorMessage || "Service Unavailable"}
                                      </Alert>
                                    </Col>
                                  </Row>
                                ) : loadingClaim ? (
                                  <div className="d-flex justify-content-center py-3">
                                    <Spinner
                                      animation="border"
                                      role="status"
                                      variant="primary"
                                    >
                                      <span className="visually-hidden">
                                        Loading…
                                      </span>
                                    </Spinner>
                                  </div>
                                ) : (
                                  <>
                                    {/* Combined rejections box — switch (tx.RJ) and
                            medical-aid (tx.ERJ) rejections stack as
                            "<type> - <description>" bullet lines in a single
                            red Alert so the user sees all rejections at once. */}
                                    {(() => {
                                      const rejections = [];
                                      if (
                                        claim?.overAllRejections?.description
                                      ) {
                                        rejections.push({
                                          type: "Switch Rejection",
                                          description:
                                            claim.overAllRejections.description,
                                        });
                                      }
                                      if (
                                        claim?.overAllFundRejections
                                          ?.description
                                      ) {
                                        rejections.push({
                                          type: "Medical Aid Rejection",
                                          description:
                                            claim.overAllFundRejections
                                              .description,
                                        });
                                      }
                                      if (rejections.length === 0) return null;
                                      return (
                                        <Alert
                                          variant="danger"
                                          className="mt-1 mb-2 mx-n2 p-2"
                                        >
                                          <div className="fw-bold">
                                            Rejections:
                                          </div>
                                          <ul className="mb-0 ps-4">
                                            {rejections.map((r, i) => (
                                              <li key={i}>
                                                {r.type} - {r.description}
                                              </li>
                                            ))}
                                          </ul>
                                        </Alert>
                                      );
                                    })()}

                                    {/* Combined info / warnings box.
                            Reversal-window and ERA-availability lines come from
                            the medikredit_list lookup snapshotted on the claim
                            at submit/reverse time. Switch/medical-aid warnings
                            and the switch's free-form message stack as
                            additional lines in the same box. Reversal and ERA
                            lines are suppressed when the claim is rejected. */}
                                    {(() => {
                                      const rejectedStatuses = [
                                        "Rejected",
                                        "Switch Rejected",
                                        "Reversal Switch Rejected",
                                      ];
                                      const isRejected =
                                        rejectedStatuses.includes(
                                          claim?.status,
                                        );
                                      // Any switch- or fund-level rejection on the claim
                                      // also suppresses the reversal line — if the claim
                                      // was rejected there's nothing to reverse.
                                      const hasAnyRejection = !!(
                                        claim?.overAllRejections?.description ||
                                        claim?.overAllFundRejections
                                          ?.description
                                      );
                                      const lines = [];
                                      // Switch/MA warnings and the switch's free-form
                                      // message come first so the user sees the claim's
                                      // own feedback before the snapshotted info lines.
                                      if (claim?.overAllWarnings?.description) {
                                        lines.push(
                                          claim.overAllWarnings.description,
                                        );
                                      }
                                      if (
                                        claim?.overAllFundWarnings?.description
                                      ) {
                                        lines.push(
                                          claim.overAllFundWarnings.description,
                                        );
                                      }
                                      if (claim?.mediKreditMsg) {
                                        lines.push(claim.mediKreditMsg);
                                      }
                                      // Reversal-window line — driven by the memoised
                                      // reversalState (above). `hide` covers both
                                      // "no reversal value on claim" and "claim is already
                                      // reversed", so we don't show a misleading countdown.
                                      // The (i) icon opens a popover explaining the
                                      // medical-aid-specific reversal policy.
                                      if (
                                        !isRejected &&
                                        !hasAnyRejection &&
                                        !reversalState.hide
                                      ) {
                                        const reversalPolicyPopover = (
                                          <Popover
                                            id="reversal-policy-popover"
                                            className="services-popover"
                                          >
                                            <Popover.Header as="h6">
                                              Reversal Policy
                                            </Popover.Header>
                                            <Popover.Body>
                                              {invoice?.medicalAid?.name ||
                                                "This medical aid"}{" "}
                                              only allows reversals within{" "}
                                              {claim?.reversal} day(s).
                                            </Popover.Body>
                                          </Popover>
                                        );
                                        const infoIcon = (
                                          <OverlayTrigger
                                            trigger={["hover", "focus"]}
                                            placement="top"
                                            rootClose
                                            overlay={reversalPolicyPopover}
                                          >
                                            <i
                                              className="fa-light fa-circle-info ms-2"
                                              style={{ cursor: "help" }}
                                              tabIndex={0}
                                            />
                                          </OverlayTrigger>
                                        );
                                        if (reversalState.expired) {
                                          lines.push(
                                            <>
                                              The claim can no longer be
                                              reversed.{infoIcon}
                                            </>,
                                          );
                                        } else {
                                          lines.push(
                                            <>
                                              The claim can still be reversed
                                              within{" "}
                                              <strong>
                                                {reversalState.remainingText}
                                              </strong>
                                              .{infoIcon}
                                            </>,
                                          );
                                        }
                                      }
                                      if (
                                        !isRejected &&
                                        claim?.era === false &&
                                        invoice?.medicalAid?.name
                                      ) {
                                        const eraPolicyPopover = (
                                          <Popover
                                            id="era-policy-popover"
                                            className="services-popover"
                                          >
                                            <Popover.Header as="h6">
                                              ERA Policy
                                            </Popover.Header>
                                            <Popover.Body>
                                              {invoice.medicalAid.name} does not
                                              support electronic remittance
                                              advices.
                                            </Popover.Body>
                                          </Popover>
                                        );
                                        lines.push(
                                          <>
                                            ERA&apos;s not supported.
                                            <OverlayTrigger
                                              trigger={["hover", "focus"]}
                                              placement="top"
                                              rootClose
                                              overlay={eraPolicyPopover}
                                            >
                                              <i
                                                className="fa-light fa-circle-info ms-2"
                                                style={{ cursor: "help" }}
                                                tabIndex={0}
                                              />
                                            </OverlayTrigger>
                                          </>,
                                        );
                                      }
                                      if (lines.length === 0) return null;
                                      return (
                                        <div className="posted-invoice-info mt-1 mb-2 mx-n2">
                                          <div className="fw-bold">
                                            Messages:
                                          </div>
                                          <ul className="mb-0 ps-4">
                                            {lines.map((line, i) => (
                                              <li key={i}>{line}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      );
                                    })()}

                                    {/* Transaction & HNET */}
                                    <Row className="mb-1">
                                      <Col xs={6} className="text-primary">
                                        Transaction #:
                                      </Col>
                                      <Col xs={6}>
                                        {claim?.transactionNumber || "-"}
                                      </Col>
                                    </Row>
                                    <Row className="mb-1">
                                      <Col xs={6} className="text-primary">
                                        HNET #:
                                      </Col>
                                      <Col xs={6}>
                                        {claim?.hnetNumber || "-"}
                                      </Col>
                                    </Row>

                                    {/* Messages */}
                                    <Row className="text-primary mt-1 mb-1 ms-1">
                                      {claim?.message}
                                    </Row>

                                    {/* Financials */}
                                    {claim?.financials?.approvedGross !=
                                      null && (
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Approved Gross:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-primary text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(
                                            claim.financials.approvedGross,
                                          )}
                                        </Col>
                                      </Row>
                                    )}
                                    {claim?.financials?.copay != null && (
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Copay:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-danger text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(claim.financials.copay)}
                                        </Col>
                                      </Row>
                                    )}
                                    {claim?.financials?.levy != null && (
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Levy:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-danger text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(claim.financials.levy)}
                                        </Col>
                                      </Row>
                                    )}
                                    {claim?.financials?.discount != null && (
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Discount:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-danger text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(claim?.financials.discount)}
                                        </Col>
                                      </Row>
                                    )}
                                    {claim?.financials?.approvedNett !=
                                      null && (
                                      <Row className="gx-0 mb-1 pt-1 border-top border-primary border-2">
                                        <Col
                                          xs={6}
                                          className="fw-bold text-primary"
                                        >
                                          Approved Nett:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-primary fw-bold text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(
                                            claim?.financials.approvedNett,
                                          )}
                                        </Col>
                                      </Row>
                                    )}
                                    {claim?.financials?.submittedAmount !=
                                      null && (
                                      <Row className="gx-0 mb-1 pb-1 ">
                                        <Col
                                          xs={6}
                                          className="fw-bold text-primary"
                                        >
                                          Submitted Amount:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-primary fw-bold text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(
                                            claim?.financials.submittedAmount,
                                          )}
                                        </Col>
                                      </Row>
                                    )}
                                    {claim?.financials?.surcharges?.schm !=
                                      null && (
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Surcharged:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-danger text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(
                                            claim?.financials.surcharges.schm,
                                          )}
                                        </Col>
                                      </Row>
                                    )}
                                    {claim?.financials?.surcharges?.save !=
                                      null && (
                                      <Row className="gx-0 mb-1">
                                        <Col
                                          xs={6}
                                          className="fw-normal text-primary"
                                        >
                                          Surcharge paid from savings:
                                        </Col>
                                        <Col
                                          xs={6}
                                          className="text-danger text-end"
                                        >
                                          {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(
                                            claim?.financials.surcharges.save,
                                          )}
                                        </Col>
                                      </Row>
                                    )}

                                    {(claim?.patientClaimFinancials
                                      ?.claimFinancialDescription ||
                                      claim?.patientClaimFinancials
                                        ?.claimFinancialAmount) && (
                                      <>
                                        <Row className="gx-0 mt-3 mb-1 ms-1 border-bottom border-primary">
                                          <Col
                                            xs={6}
                                            className="fw-bold text-primary"
                                          >
                                            Patient Financials:
                                          </Col>
                                        </Row>
                                        {claim?.patientClaimFinancials
                                          .claimFinancialDescription && (
                                          <Row className="gx-0 mb-1">
                                            <Col
                                              xs={6}
                                              className="fw-normal text-primary"
                                            >
                                              Description:
                                            </Col>
                                            <Col xs={6} className="text-end">
                                              {
                                                claim?.patientClaimFinancials
                                                  ?.claimFinancialDescription
                                              }
                                            </Col>
                                          </Row>
                                        )}
                                        {claim?.patientClaimFinancials
                                          .claimFinancialAmount && (
                                          <Row className="gx-0 mb-1 border-bottom border-primary border-1">
                                            <Col
                                              xs={6}
                                              className="fw-normal text-primary"
                                            >
                                              Amount:
                                            </Col>
                                            <Col xs={6} className="text-end ">
                                              {new Intl.NumberFormat("en-ZA", {
                                                style: "currency",
                                                currency: "ZAR",
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }).format(
                                                claim?.patientClaimFinancials
                                                  ?.claimFinancialAmount,
                                              )}
                                            </Col>
                                          </Row>
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                      </Accordion>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {showReceiptModal && account && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={account}
        />
      )}
      {showEmailModal && statementBlob && (
        <EmailInvoiceModal
          show={showEmailModal}
          onHide={() => setShowEmailModal(false)}
          defaultEmail={account.accountEmail}
          email2={account.accountEmail2}
          email3={account.accountEmail3}
          email4={account.accountEmail4}
          email5={account.accountEmail5}
          onSend={async ({
            email,
            email2,
            email3,
            email4,
            email5,
            type = "invoice",
          }) => {
            try {
              const emailData = buildEmailData({ invoice, account, practice });

              // Collect all non-empty emails into an array
              const emails = [email, email2, email3, email4, email5]
                .map((e) => (e || "").trim())
                .filter(Boolean);

              const result = await sendInvoiceByEmail({
                emails,
                pdfBlob: statementBlob,
                type,
                accountId: account?._id,
                emailData,
              });
              console.log("ED", emailData);
              if (result?.error) throw new Error(result.error);
            } catch (err) {
              console.error("⛔ Failed to send email:", err);
              throw err;
            }
          }}
        />
      )}

      {liabilityModalShow && (
        <ChangeLiability
          show={liabilityModalShow}
          onHide={() => setLiabilityModalShow(false)}
          lines={invoice?.lines}
          invoiceId={invoice?._id}
          invoice={invoice}
          claimId={claim?._id}
          claim={claim}
          account={account}
          practice={practice}
          patient={patient}
          userId={userId}
        />
      )}

      {/* Invoice deletion modal — block rules + role-aware confirmation
          live inside the modal. On success we navigate back to the
          account statement view. */}
      <InvoiceDeletionModal
        show={showInvoiceDeletionModal}
        onHide={() => setShowInvoiceDeletionModal(false)}
        invoice={invoice}
        onDeleted={({ status }) => {
          if (status === "done") {
            setTimeout(() => {
              if (account?._id) navigate(`/accounts/${account._id}`);
            }, 800);
          }
        }}
        onOpenReceiptReversal={() => setShowReceiptReversalModal(true)}
      />

      {/* Receipt reversal modal — hosted locally so the deletion modal's
          "Reverse receipt" hand-off is just state, not cross-page nav. */}
      {showReceiptReversalModal && account && (
        <ReceiptReversalModal
          show={showReceiptReversalModal}
          onHide={() => setShowReceiptReversalModal(false)}
          account={account}
        />
      )}
    </div>
  );
}
